import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const GOAL_STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'completed', label: 'Concluido' },
  { value: 'paused', label: 'Pausado' },
];

const GOAL_STATUS_TONES = {
  active: 'border-sky-200 bg-sky-50 text-sky-700',
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  paused: 'border-slate-200 bg-slate-100 text-slate-600',
};

const KR_STATUS_TONES = {
  on_track: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  at_risk: 'border-amber-200 bg-amber-50 text-amber-700',
  behind: 'border-rose-200 bg-rose-50 text-rose-700',
  completed: 'border-blue-200 bg-blue-50 text-blue-700',
};

const HEALTH_TONES = {
  on_track: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  at_risk: 'border-amber-200 bg-amber-50 text-amber-700',
  behind: 'border-rose-200 bg-rose-50 text-rose-700',
  completed: 'border-blue-200 bg-blue-50 text-blue-700',
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'completed', label: 'Concluidas' },
  { value: 'paused', label: 'Pausadas' },
  { value: 'automated', label: 'Automatizadas' },
  { value: 'attention', label: 'Em risco' },
];

const METRIC_OPTIONS = [
  { value: 'completed_items', label: 'Itens concluidos', unit: 'numero', comparison: 'gte', description: 'Conta tarefas e subtarefas concluidas nos boards vinculados.' },
  { value: 'progress_percentage', label: 'Progresso real', unit: '%', comparison: 'gte', description: 'Usa o percentual consolidado do trabalho entregue.' },
  { value: 'overdue_items', label: 'Itens atrasados', unit: 'numero', comparison: 'lte', description: 'Monitora o volume de tarefas e subtarefas vencidas.' },
  { value: 'blocked_items', label: 'Itens bloqueados', unit: 'numero', comparison: 'lte', description: 'Sinaliza gargalos operacionais que travam a execucao.' },
  { value: 'tracked_minutes', label: 'Tempo rastreado', unit: 'min', comparison: 'gte', description: 'Soma o tempo efetivamente rastreado nas entregas.' },
  { value: 'planned_minutes', label: 'Carga planejada', unit: 'min', comparison: 'gte', description: 'Acumula a carga prevista para o escopo vinculado.' },
  { value: 'unassigned_items', label: 'Itens sem responsavel', unit: 'numero', comparison: 'lte', description: 'Mede o backlog sem dono definido.' },
  { value: 'subtasks', label: 'Subtarefas', unit: 'numero', comparison: 'gte', description: 'Conta subtarefas mapeadas no objetivo.' },
  { value: 'due_today', label: 'Vencem hoje', unit: 'numero', comparison: 'lte', description: 'Foco em prazo imediato no dia atual.' },
  { value: 'due_this_week', label: 'Vencem na semana', unit: 'numero', comparison: 'lte', description: 'Pressao de prazo dentro da semana atual.' },
];

const UNIT_OPTIONS = ['%', 'numero', 'R$', 'pts', 'min'];

function emptyGoalForm() {
  return {
    title: '',
    description: '',
    board_ids: [],
    key_results: [],
    status: 'active',
    start_date: '',
    end_date: '',
  };
}

function emptyKRForm() {
  return {
    id: null,
    title: '',
    source_type: 'manual',
    metric_key: 'completed_items',
    current: 0,
    target: 100,
    unit: '%',
    comparison_operator: 'gte',
    board_ids: [],
  };
}

function isAutomatedKR(kr) {
  return (kr?.source_type || 'manual') === 'board_metric' || Boolean(kr?.auto_update) || Boolean(kr?.metric_key && kr?.source_type === 'board_metric');
}

function formatMetricValue(value, unit) {
  const safe = Number(value || 0);
  if (unit === 'R$') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(safe);
  }
  if (unit === '%') return `${Math.round(safe)}%`;
  if (unit === 'min') {
    const hours = Math.floor(safe / 60);
    const minutes = safe % 60;
    if (!hours) return `${minutes}m`;
    if (!minutes) return `${hours}h`;
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${Math.round((safe + Number.EPSILON) * 10) / 10}${unit && unit !== 'numero' ? ` ${unit}` : ''}`;
}

function progressForKR(kr) {
  if (kr?.progress_percentage !== undefined && kr?.progress_percentage !== null) {
    return Math.max(0, Math.min(100, Number(kr.progress_percentage) || 0));
  }
  const current = Number(kr?.current || 0);
  const target = Number(kr?.target || 0);
  const comparison = kr?.comparison_operator || 'gte';
  if (comparison === 'lte') {
    if (current <= target) return 100;
    if (current <= 0) return 100;
    return Math.max(0, Math.min(100, (target / current) * 100));
  }
  if (target <= 0) return current > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, (current / target) * 100));
}

function goalProgress(goal) {
  if (goal?.progress_percentage !== undefined && goal?.progress_percentage !== null) {
    return Math.max(0, Math.min(100, Number(goal.progress_percentage) || 0));
  }
  const keyResults = goal?.key_results || [];
  if (!keyResults.length) return 0;
  const total = keyResults.reduce((sum, kr) => sum + progressForKR(kr), 0);
  return Math.round(total / keyResults.length);
}

function metricOptionFor(metricKey) {
  return METRIC_OPTIONS.find(option => option.value === metricKey);
}

function serializeKeyResult(kr) {
  return {
    id: kr.id || `kr_${Date.now()}`,
    title: kr.title,
    current: Number(kr.current || 0),
    target: Number(kr.target || 0),
    unit: kr.unit || 'numero',
    status: kr.status || 'on_track',
    source_type: kr.source_type || 'manual',
    metric_key: kr.source_type === 'board_metric' ? kr.metric_key || 'completed_items' : null,
    board_ids: kr.source_type === 'board_metric' ? (kr.board_ids || []) : [],
    comparison_operator: kr.comparison_operator || 'gte',
    auto_update: kr.source_type === 'board_metric',
    history: kr.history || [],
  };
}

export default function GoalsOKR({ api, boards }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingGoal, setSavingGoal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState(emptyGoalForm());
  const [krForm, setKrForm] = useState(emptyKRForm());
  const [editingKrId, setEditingKrId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showKRModal, setShowKRModal] = useState(null);
  const [krValue, setKrValue] = useState('');
  const [krNote, setKrNote] = useState('');
  const [updatingKR, setUpdatingKR] = useState(false);
  const [updatingGoalStatusId, setUpdatingGoalStatusId] = useState(null);
  const [aiLoadingGoalId, setAiLoadingGoalId] = useState(null);
  const [draftingAI, setDraftingAI] = useState(false);
  const [aiDraftSummary, setAiDraftSummary] = useState('');

  const boardMap = useMemo(
    () => new Map((boards || []).map(board => [board.id, board])),
    [boards]
  );

  const metricsMap = useMemo(
    () => new Map(METRIC_OPTIONS.map(option => [option.value, option])),
    []
  );

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects/goals');
      setGoals(response.data || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error('Erro ao carregar metas e OKRs');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingGoal(null);
    setGoalForm(emptyGoalForm());
    setKrForm(emptyKRForm());
    setEditingKrId(null);
    setAiDraftSummary('');
    setShowCreateModal(true);
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title || '',
      description: goal.description || '',
      board_ids: goal.board_ids || [],
      key_results: (goal.key_results || []).map(serializeKeyResult),
      status: goal.status || 'active',
      start_date: goal.start_date ? String(goal.start_date).substring(0, 10) : '',
      end_date: goal.end_date ? String(goal.end_date).substring(0, 10) : '',
    });
    setKrForm(emptyKRForm());
    setEditingKrId(null);
    setAiDraftSummary(goal.ai_summary || '');
    setShowCreateModal(true);
  };

  const resetKRBuilder = () => {
    setKrForm(emptyKRForm());
    setEditingKrId(null);
  };

  const toggleGoalBoard = (boardId) => {
    setGoalForm(current => ({
      ...current,
      board_ids: current.board_ids.includes(boardId)
        ? current.board_ids.filter(id => id !== boardId)
        : [...current.board_ids, boardId],
    }));
  };

  const toggleKRBoard = (boardId) => {
    setKrForm(current => ({
      ...current,
      board_ids: current.board_ids.includes(boardId)
        ? current.board_ids.filter(id => id !== boardId)
        : [...current.board_ids, boardId],
    }));
  };

  const handleMetricChange = (metricKey) => {
    const metric = metricOptionFor(metricKey);
    setKrForm(current => ({
      ...current,
      metric_key: metricKey,
      comparison_operator: metric?.comparison || 'gte',
      unit: metric?.unit || current.unit,
    }));
  };

  const addOrUpdateKR = () => {
    if (!krForm.title.trim()) {
      toast.error('Defina um nome para o key result');
      return;
    }

    if (krForm.source_type === 'board_metric' && !goalForm.board_ids.length && !krForm.board_ids.length) {
      toast.error('Vincule ao menos um board ao objetivo ou ao KR automatizado');
      return;
    }

    const normalizedKR = serializeKeyResult({
      ...krForm,
      id: editingKrId || krForm.id || `kr_${Date.now()}`,
      current: krForm.source_type === 'board_metric' ? 0 : Number(krForm.current || 0),
      unit: krForm.source_type === 'board_metric'
        ? (metricOptionFor(krForm.metric_key)?.unit || krForm.unit || 'numero')
        : krForm.unit,
    });

    setGoalForm(current => {
      const nextKeyResults = editingKrId
        ? current.key_results.map(kr => (kr.id === editingKrId ? normalizedKR : kr))
        : [...current.key_results, normalizedKR];
      return { ...current, key_results: nextKeyResults };
    });

    resetKRBuilder();
  };

  const editKR = (kr) => {
    setEditingKrId(kr.id);
    setKrForm({
      id: kr.id,
      title: kr.title || '',
      source_type: kr.source_type || (isAutomatedKR(kr) ? 'board_metric' : 'manual'),
      metric_key: kr.metric_key || 'completed_items',
      current: Number(kr.current || 0),
      target: Number(kr.target || 0),
      unit: kr.unit || 'numero',
      comparison_operator: kr.comparison_operator || 'gte',
      board_ids: kr.board_ids || [],
    });
  };

  const removeKR = (krId) => {
    setGoalForm(current => ({
      ...current,
      key_results: current.key_results.filter(kr => kr.id !== krId),
    }));
    if (editingKrId === krId) {
      resetKRBuilder();
    }
  };

  const handleSaveGoal = async () => {
    if (!goalForm.title.trim()) {
      toast.error('O objetivo precisa de um titulo');
      return;
    }
    if (!goalForm.key_results.length) {
      toast.error('Adicione pelo menos um key result');
      return;
    }

    setSavingGoal(true);
    try {
      const payload = {
        title: goalForm.title.trim(),
        description: goalForm.description.trim(),
        board_ids: goalForm.board_ids,
        status: goalForm.status,
        key_results: goalForm.key_results.map(serializeKeyResult),
        start_date: goalForm.start_date ? new Date(`${goalForm.start_date}T12:00:00`).toISOString() : null,
        end_date: goalForm.end_date ? new Date(`${goalForm.end_date}T12:00:00`).toISOString() : null,
      };

      let response;
      if (editingGoal) {
        response = await api.put(`/projects/goals/${editingGoal.id}`, payload);
        setGoals(current => current.map(goal => goal.id === editingGoal.id ? response.data : goal));
        toast.success('Meta atualizada');
      } else {
        response = await api.post('/projects/goals', payload);
        setGoals(current => [response.data, ...current]);
        toast.success('Meta criada');
      }

      setShowCreateModal(false);
      setEditingGoal(null);
      setGoalForm(emptyGoalForm());
      resetKRBuilder();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Erro ao salvar meta');
    } finally {
      setSavingGoal(false);
    }
  };

  const handleGenerateDraftWithAI = async () => {
    if (!goalForm.title.trim()) {
      toast.error('Preencha o titulo do objetivo antes de gerar sugestoes');
      return;
    }
    if (!goalForm.description.trim()) {
      toast.error('Descreva o objetivo para a IA entender o contexto');
      return;
    }
    if (!goalForm.board_ids.length) {
      toast.error('Selecione ao menos um board para amarrar os KRs as colunas existentes');
      return;
    }

    if (goalForm.key_results.length > 0 && !window.confirm('Substituir os KRs atuais pelas sugestoes da IA?')) {
      return;
    }

    setDraftingAI(true);
    try {
      const response = await api.post('/projects/goals/ai-draft', {
        title: goalForm.title.trim(),
        description: goalForm.description.trim(),
        board_ids: goalForm.board_ids,
      });

      const suggestedKeyResults = (response.data?.key_results || []).map(serializeKeyResult);
      setGoalForm(current => ({
        ...current,
        key_results: suggestedKeyResults,
      }));
      setAiDraftSummary(response.data?.ai_summary || '');
      resetKRBuilder();
      toast.success('Sugestoes de OKR geradas com IA');
    } catch (error) {
      console.error('Error generating OKR draft:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao gerar sugestoes com IA');
    } finally {
      setDraftingAI(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Excluir esta meta e manter apenas o historico das tarefas no board?')) return;
    try {
      await api.delete(`/projects/goals/${goalId}`);
      setGoals(current => current.filter(goal => goal.id !== goalId));
      toast.success('Meta excluida');
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Erro ao excluir meta');
    }
  };

  const handleStatusChange = async (goalId, status) => {
    setUpdatingGoalStatusId(goalId);
    try {
      const response = await api.put(`/projects/goals/${goalId}`, { status });
      setGoals(current => current.map(goal => goal.id === goalId ? response.data : goal));
    } catch (error) {
      console.error('Error updating goal status:', error);
      toast.error('Erro ao atualizar status da meta');
    } finally {
      setUpdatingGoalStatusId(null);
    }
  };

  const openKRUpdate = (goal, kr) => {
    if (isAutomatedKR(kr)) return;
    setShowKRModal({ goal, kr });
    setKrValue(String(kr.current || 0));
    setKrNote('');
  };

  const handleUpdateKR = async () => {
    if (!showKRModal) return;
    setUpdatingKR(true);
    try {
      const response = await api.put(
        `/projects/goals/${showKRModal.goal.id}/key-results/${showKRModal.kr.id}`,
        {
          current: parseFloat(krValue) || 0,
          note: krNote || null,
        }
      );
      setGoals(current => current.map(goal => goal.id === showKRModal.goal.id ? response.data : goal));
      setShowKRModal(null);
      setKrNote('');
      toast.success('Key result atualizado');
    } catch (error) {
      console.error('Error updating key result:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao atualizar key result');
    } finally {
      setUpdatingKR(false);
    }
  };

  const handleGenerateAI = async (goalId) => {
    setAiLoadingGoalId(goalId);
    try {
      const response = await api.post(`/projects/goals/${goalId}/ai-insights`);
      setGoals(current => current.map(goal => goal.id === goalId ? response.data : goal));
      toast.success('Leitura de IA atualizada');
    } catch (error) {
      console.error('Error generating AI insights:', error);
      toast.error('Erro ao gerar leitura de IA');
    } finally {
      setAiLoadingGoalId(null);
    }
  };

  const filteredGoals = useMemo(() => {
    if (filter === 'all') return goals;
    if (filter === 'automated') return goals.filter(goal => Number(goal.automated_key_results || 0) > 0);
    if (filter === 'attention') return goals.filter(goal => ['at_risk', 'behind'].includes(goal.health_status));
    return goals.filter(goal => goal.status === filter);
  }, [filter, goals]);

  const summary = useMemo(() => {
    const automatedKRs = goals.reduce((sum, goal) => sum + Number(goal.automated_key_results || 0), 0);
    const totalKRs = goals.reduce((sum, goal) => sum + (goal.key_results || []).length, 0);
    const linkedBoards = new Set(goals.flatMap(goal => goal.board_ids || []));
    const attentionGoals = goals.filter(goal => ['at_risk', 'behind'].includes(goal.health_status)).length;

    return {
      totalGoals: goals.length,
      automatedKRs,
      totalKRs,
      linkedBoards: linkedBoards.size,
      attentionGoals,
    };
  }, [goals]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <section className="min-h-[calc(100vh-190px)] px-3 py-4 sm:px-5 lg:px-7">
      <div className="mx-auto w-full max-w-none space-y-6">
        <header className="rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_30%),linear-gradient(135deg,_#0f172a,_#1e293b_58%,_#334155)] px-6 py-6 text-white shadow-sm">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">Metas inteligentes</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight">OKRs conectados ao trabalho real</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-300">
                Objetivos com key results manuais e automatizados por tarefas, subtarefas, carga, prazo e risco operacional.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">OKRs ativos</p>
                <p className="mt-1 text-2xl font-semibold">{summary.totalGoals}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">KRs automatizados</p>
                <p className="mt-1 text-2xl font-semibold">{summary.automatedKRs}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Boards vinculados</p>
                <p className="mt-1 text-2xl font-semibold">{summary.linkedBoards}</p>
              </div>
              <Button onClick={openCreate} className="bg-white text-slate-950 hover:bg-slate-100">
                Nova meta
              </Button>
            </div>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-4">
          <Card className="rounded-3xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Cobertura</p>
              <p className="mt-3 text-3xl font-bold text-slate-900">{summary.totalKRs}</p>
              <p className="mt-2 text-sm text-slate-500">key results no portifolio atual</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Automacao</p>
              <p className="mt-3 text-3xl font-bold text-slate-900">{summary.automatedKRs}</p>
              <p className="mt-2 text-sm text-slate-500">KRs lendo tarefas e subtarefas automaticamente</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Em atencao</p>
              <p className="mt-3 text-3xl font-bold text-slate-900">{summary.attentionGoals}</p>
              <p className="mt-2 text-sm text-slate-500">objetivos com saude em risco ou atrasada</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">IA operacional</p>
              <p className="mt-3 text-base font-semibold text-slate-900">Cada objetivo pode gerar leitura automatica com risco e proxima acao.</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                filter === option.value
                  ? 'border-blue-200 bg-blue-50 font-medium text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {filteredGoals.length === 0 ? (
          <Card className="rounded-3xl border-slate-200 shadow-sm">
            <CardContent className="py-14 text-center">
              <div className="text-5xl">🎯</div>
              <h3 className="mt-4 text-xl font-semibold text-slate-900">
                {filter === 'all' ? 'Nenhuma meta criada ainda' : 'Nenhum objetivo corresponde ao filtro'}
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                Estruture objetivos com KRs automatizados a partir do board, sem perder o historico ja salvo.
              </p>
              <Button className="mt-5" onClick={openCreate}>Criar primeira meta</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {filteredGoals.map(goal => {
              const progress = goalProgress(goal);
              const linkedBoards = (goal.board_ids || []).map(boardId => boardMap.get(boardId)).filter(Boolean);
              const automatedCount = Number(goal.automated_key_results || 0);

              return (
                <Card key={goal.id} className="overflow-hidden rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-0">
                    <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-5 sm:px-6">
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-xl font-semibold text-slate-900">{goal.title}</h3>
                            <Badge className={`border ${GOAL_STATUS_TONES[goal.status] || GOAL_STATUS_TONES.active}`}>
                              {GOAL_STATUS_OPTIONS.find(option => option.value === goal.status)?.label || goal.status}
                            </Badge>
                            <Badge className={`border ${HEALTH_TONES[goal.health_status] || HEALTH_TONES.on_track}`}>
                              {goal.health_status === 'completed'
                                ? 'Saude concluida'
                                : goal.health_status === 'behind'
                                  ? 'Saude atrasada'
                                  : goal.health_status === 'at_risk'
                                    ? 'Saude em risco'
                                    : 'Saude no prazo'}
                            </Badge>
                          </div>

                          {goal.description && (
                            <p className="mt-2 max-w-3xl text-sm text-slate-600">{goal.description}</p>
                          )}

                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                              {automatedCount} KR(s) automatico(s)
                            </span>
                            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                              {goal.key_results?.length || 0} KR(s)
                            </span>
                            {(goal.start_date || goal.end_date) && (
                              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                                {goal.start_date ? new Date(goal.start_date).toLocaleDateString('pt-BR') : 'Sem inicio'}
                                {' -> '}
                                {goal.end_date ? new Date(goal.end_date).toLocaleDateString('pt-BR') : 'Sem fim'}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row xl:flex-col xl:items-end">
                          <Select
                            value={goal.status}
                            onValueChange={(value) => handleStatusChange(goal.id, value)}
                            disabled={updatingGoalStatusId === goal.id}
                          >
                            <SelectTrigger className="w-[160px] rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GOAL_STATUS_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={aiLoadingGoalId === goal.id}
                              onClick={() => handleGenerateAI(goal.id)}
                            >
                              {aiLoadingGoalId === goal.id ? 'Gerando IA...' : 'Atualizar IA'}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => openEdit(goal)}>
                              Editar
                            </Button>
                            <Button type="button" variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => handleDeleteGoal(goal.id)}>
                              Excluir
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>Progresso consolidado</span>
                            <span className="font-semibold text-slate-900">{Math.round(progress)}%</span>
                          </div>
                          <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full ${
                                progress >= 100 ? 'bg-blue-500' : progress >= 70 ? 'bg-emerald-500' : progress >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                            <div className="rounded-xl bg-slate-50 px-3 py-3">
                              <p className="text-slate-400">KRs</p>
                              <p className="mt-1 text-base font-semibold text-slate-900">{goal.key_results?.length || 0}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-3">
                              <p className="text-slate-400">Automaticos</p>
                              <p className="mt-1 text-base font-semibold text-slate-900">{automatedCount}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-3">
                              <p className="text-slate-400">Boards</p>
                              <p className="mt-1 text-base font-semibold text-slate-900">{goal.linked_board_count || goal.board_ids?.length || 0}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-3">
                              <p className="text-slate-400">IA</p>
                              <p className="mt-1 text-base font-semibold text-slate-900">{goal.ai_summary ? 'Ativa' : 'Pendente'}</p>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <p className="text-sm font-semibold text-slate-900">Boards que alimentam este objetivo</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {linkedBoards.length ? linkedBoards.map(board => (
                              <span key={board.id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                {board.name}
                              </span>
                            )) : (
                              <span className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500">
                                Sem boards vinculados
                              </span>
                            )}
                          </div>

                          {goal.ai_summary && (
                            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-blue-900">Leitura da IA</p>
                                {goal.ai_updated_at && (
                                  <span className="text-[11px] text-blue-700">
                                    {new Date(goal.ai_updated_at).toLocaleDateString('pt-BR')}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-sm leading-relaxed text-blue-900">{goal.ai_summary}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="px-5 py-5 sm:px-6">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Key results</p>
                          <h4 className="mt-1 text-lg font-semibold text-slate-900">Medicao automatica e operacional</h4>
                        </div>
                        <p className="text-sm text-slate-500">
                          Clique para atualizar apenas KRs manuais. KRs automatizados leem as tarefas vinculadas.
                        </p>
                      </div>

                      <div className="mt-4 space-y-3">
                        {(goal.key_results || []).map(kr => {
                          const krProgress = progressForKR(kr);
                          const automated = isAutomatedKR(kr);
                          const metric = metricsMap.get(kr.metric_key);
                          const krBoards = (kr.board_ids && kr.board_ids.length ? kr.board_ids : goal.board_ids || [])
                            .map(boardId => boardMap.get(boardId)?.name)
                            .filter(Boolean);

                          return (
                            <article
                              key={kr.id}
                              className={`rounded-2xl border p-4 transition-colors ${automated ? 'border-slate-200 bg-slate-50/70' : 'cursor-pointer border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'}`}
                              onClick={() => openKRUpdate(goal, kr)}
                            >
                              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="font-semibold text-slate-900">{kr.title}</p>
                                    <Badge className={`border ${KR_STATUS_TONES[kr.status] || KR_STATUS_TONES.on_track}`}>
                                      {kr.status === 'completed'
                                        ? 'Concluido'
                                        : kr.status === 'behind'
                                          ? 'Atrasado'
                                          : kr.status === 'at_risk'
                                            ? 'Em risco'
                                            : 'No prazo'}
                                    </Badge>
                                    <Badge variant="outline" className="border-slate-200 text-slate-600">
                                      {automated ? 'Automatico' : 'Manual'}
                                    </Badge>
                                  </div>

                                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                    {automated && (
                                      <span className="rounded-full bg-white px-2.5 py-1">
                                        {kr.metric_label || metric?.label || kr.metric_key}
                                      </span>
                                    )}
                                    {automated && (
                                      <span className="rounded-full bg-white px-2.5 py-1">
                                        {krBoards.length ? `${krBoards.length} board(s)` : 'Usa boards do objetivo'}
                                      </span>
                                    )}
                                    {!automated && kr.history?.length > 0 && (
                                      <span className="rounded-full bg-white px-2.5 py-1">
                                        {kr.history.length} atualizacao(oes)
                                      </span>
                                    )}
                                  </div>

                                  {automated && metric?.description && (
                                    <p className="mt-2 text-sm text-slate-500">{metric.description}</p>
                                  )}
                                </div>

                                <div className="w-full max-w-[260px] rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Atual</span>
                                    <span className="font-semibold text-slate-900">{formatMetricValue(kr.current, kr.unit)}</span>
                                  </div>
                                  <div className="mt-1 flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Meta</span>
                                    <span className="font-semibold text-slate-900">{formatMetricValue(kr.target, kr.unit)}</span>
                                  </div>
                                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                                    <div
                                      className={`h-full rounded-full ${
                                        krProgress >= 100 ? 'bg-blue-500' : krProgress >= 70 ? 'bg-emerald-500' : krProgress >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                      }`}
                                      style={{ width: `${krProgress}%` }}
                                    />
                                  </div>
                                  <p className="mt-2 text-right text-xs font-semibold text-slate-600">{Math.round(krProgress)}%</p>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-1rem)] max-w-[1380px] flex-col overflow-hidden border-slate-200 p-0 sm:w-[calc(100vw-2rem)]">
          <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
            <DialogTitle>{editingGoal ? 'Editar meta e OKRs' : 'Nova meta e OKRs'}</DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
            <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
              <div className="space-y-6">
                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Objetivo</p>
                        <p className="mt-1 text-sm text-slate-500">A IA usa este contexto para sugerir KRs amarrados aos boards e colunas existentes.</p>
                      </div>
                      <Button type="button" variant="outline" onClick={handleGenerateDraftWithAI} disabled={draftingAI}>
                        {draftingAI ? 'Gerando com IA...' : 'Gerar KRs com IA'}
                      </Button>
                    </div>
                    <div className="mt-4 space-y-4">
                      <div>
                        <Label>Titulo do objetivo</Label>
                        <Input className="mt-2" value={goalForm.title} onChange={event => setGoalForm(current => ({ ...current, title: event.target.value }))} placeholder="Ex.: Reduzir atraso operacional do portfolio" />
                      </div>
                      <div>
                        <Label>Descricao</Label>
                        <Textarea className="mt-2 min-h-[110px]" value={goalForm.description} onChange={event => setGoalForm(current => ({ ...current, description: event.target.value }))} placeholder="Contexto, criterio de sucesso e principais frentes ligadas ao objetivo." />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <Label>Status</Label>
                          <Select value={goalForm.status} onValueChange={value => setGoalForm(current => ({ ...current, status: value }))}>
                            <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GOAL_STATUS_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Inicio</Label>
                          <Input type="date" className="mt-2" value={goalForm.start_date} onChange={event => setGoalForm(current => ({ ...current, start_date: event.target.value }))} />
                        </div>
                        <div>
                          <Label>Fim</Label>
                          <Input type="date" className="mt-2" value={goalForm.end_date} onChange={event => setGoalForm(current => ({ ...current, end_date: event.target.value }))} />
                        </div>
                      </div>

                      {aiDraftSummary && (
                        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                          <p className="text-sm font-semibold text-blue-900">Leitura da sugestao</p>
                          <p className="mt-2 text-sm leading-relaxed text-blue-900">{aiDraftSummary}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Boards conectados</p>
                    <p className="mt-1 text-sm text-slate-500">Selecione os boards que alimentam automaticamente este objetivo.</p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {(boards || []).map(board => {
                        const selected = goalForm.board_ids.includes(board.id);
                        return (
                          <button
                            key={board.id}
                            type="button"
                            onClick={() => toggleGoalBoard(board.id)}
                            className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                              selected
                                ? 'border-blue-200 bg-blue-50 ring-2 ring-blue-500/10'
                                : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                            }`}
                          >
                            <p className="font-semibold text-slate-900">{board.name}</p>
                            <p className="mt-1 text-xs text-slate-500">{selected ? 'Vinculado ao objetivo' : 'Disponivel para alimentar KRs'}</p>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Builder de KR</p>
                        <h3 className="mt-1 text-lg font-semibold text-slate-900">Monte KRs manuais ou automatizados</h3>
                      </div>
                      {editingKrId && (
                        <Button type="button" variant="outline" size="sm" onClick={resetKRBuilder}>
                          Cancelar edicao
                        </Button>
                      )}
                    </div>

                    <div className="mt-5 space-y-4">
                      <div>
                        <Label>Nome do key result</Label>
                        <Input className="mt-2" value={krForm.title} onChange={event => setKrForm(current => ({ ...current, title: event.target.value }))} placeholder="Ex.: Reduzir itens atrasados para menos de 10" />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label>Tipo de medicao</Label>
                          <Select
                            value={krForm.source_type}
                            onValueChange={value => setKrForm(current => ({
                              ...current,
                              source_type: value,
                              comparison_operator: value === 'board_metric'
                                ? (metricOptionFor(current.metric_key)?.comparison || 'gte')
                                : current.comparison_operator,
                              unit: value === 'board_metric'
                                ? (metricOptionFor(current.metric_key)?.unit || current.unit)
                                : current.unit,
                            }))}
                          >
                            <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">Manual</SelectItem>
                              <SelectItem value="board_metric">Automatico por tarefas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {krForm.source_type === 'board_metric' ? (
                          <div>
                            <Label>Indicador operacional</Label>
                            <Select value={krForm.metric_key} onValueChange={handleMetricChange}>
                              <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {METRIC_OPTIONS.map(option => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div>
                            <Label>Valor atual</Label>
                            <Input type="number" className="mt-2" value={krForm.current} onChange={event => setKrForm(current => ({ ...current, current: parseFloat(event.target.value) || 0 }))} />
                          </div>
                        )}
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <Label>Meta</Label>
                          <Input type="number" className="mt-2" value={krForm.target} onChange={event => setKrForm(current => ({ ...current, target: parseFloat(event.target.value) || 0 }))} />
                        </div>
                        <div>
                          <Label>Unidade</Label>
                          <Select value={krForm.unit} onValueChange={value => setKrForm(current => ({ ...current, unit: value }))}>
                            <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {UNIT_OPTIONS.map(unit => (
                                <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Regra de comparacao</Label>
                          <Select value={krForm.comparison_operator} onValueChange={value => setKrForm(current => ({ ...current, comparison_operator: value }))}>
                            <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gte">Quanto maior melhor</SelectItem>
                              <SelectItem value="lte">Quanto menor melhor</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {krForm.source_type === 'board_metric' && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                          <p className="text-sm font-semibold text-slate-900">Escopo do KR automatico</p>
                          <p className="mt-1 text-sm text-slate-500">
                            Se nao selecionar boards aqui, o KR usa todos os boards do objetivo.
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(boards || []).map(board => {
                              const selected = krForm.board_ids.includes(board.id);
                              return (
                                <button
                                  key={board.id}
                                  type="button"
                                  onClick={() => toggleKRBoard(board.id)}
                                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                    selected
                                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                                  }`}
                                >
                                  {board.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                        <Button type="button" variant="outline" onClick={resetKRBuilder}>
                          Limpar builder
                        </Button>
                        <Button type="button" onClick={addOrUpdateKR}>
                          {editingKrId ? 'Salvar KR' : 'Adicionar KR'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-3xl border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">KRs configurados</p>
                    <div className="mt-4 space-y-3">
                      {goalForm.key_results.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                          Nenhum KR configurado ainda.
                        </div>
                      )}

                      {goalForm.key_results.map(kr => (
                        <div key={kr.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-slate-900">{kr.title}</p>
                                <Badge variant="outline" className="border-slate-200 text-slate-600">
                                  {isAutomatedKR(kr) ? 'Automatico' : 'Manual'}
                                </Badge>
                              </div>
                              <p className="mt-2 text-sm text-slate-500">
                                {isAutomatedKR(kr)
                                  ? `${metricOptionFor(kr.metric_key)?.label || kr.metric_key} · ${kr.board_ids?.length ? `${kr.board_ids.length} board(s)` : 'usa boards do objetivo'}`
                                  : `${formatMetricValue(kr.current, kr.unit)} -> ${formatMetricValue(kr.target, kr.unit)}`}
                              </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={() => editKR(kr)}>
                                Editar
                              </Button>
                              <Button type="button" variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => removeKR(kr.id)}>
                                Remover
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                      <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                        Cancelar
                      </Button>
                      <Button type="button" onClick={handleSaveGoal} disabled={savingGoal}>
                        {savingGoal ? 'Salvando...' : editingGoal ? 'Salvar alteracoes' : 'Criar meta'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(showKRModal)} onOpenChange={() => setShowKRModal(null)}>
        <DialogContent className="max-w-lg border-slate-200">
          <DialogHeader>
            <DialogTitle>Atualizar KR manual</DialogTitle>
          </DialogHeader>
          {showKRModal && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="font-semibold text-slate-900">{showKRModal.kr.title}</p>
                <p className="mt-1 text-sm text-slate-500">
                  Meta: {formatMetricValue(showKRModal.kr.target, showKRModal.kr.unit)}
                </p>
              </div>

              <div>
                <Label>Valor atual</Label>
                <Input type="number" className="mt-2" value={krValue} onChange={event => setKrValue(event.target.value)} />
              </div>

              <div>
                <Label>Evidencia ou observacao</Label>
                <Textarea className="mt-2 min-h-[120px]" value={krNote} onChange={event => setKrNote(event.target.value)} placeholder="Explique o que gerou esse avanço para manter o historico util." />
              </div>

              {showKRModal.kr.history?.length > 0 && (
                <div>
                  <Label>Historico</Label>
                  <div className="mt-2 max-h-44 space-y-2 overflow-y-auto pr-1">
                    {[...showKRModal.kr.history].reverse().map((entry, index) => (
                      <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-slate-800">{entry.previous} -> {entry.value}</span>
                          <span className="text-xs text-slate-400">{new Date(entry.date).toLocaleDateString('pt-BR')}</span>
                        </div>
                        {entry.note && <p className="mt-1 text-xs text-slate-500">{entry.note}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setShowKRModal(null)}>
                  Cancelar
                </Button>
                <Button type="button" onClick={handleUpdateKR} disabled={updatingKR}>
                  {updatingKR ? 'Salvando...' : 'Salvar progresso'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
